import { useEffect, useMemo, useState } from "react";
import {
  Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid, Legend,
} from "recharts";

import { PageHeader } from "@/components/PageHeader";
import { useTenant } from "@/contexts/TenantContext";
import { api, getActiveBranchName } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

import { DateRangeFilter, presetRange } from "@/components/reports/DateRangeFilter";
import { BranchFilter } from "@/components/reports/BranchFilter";
import { KpiCard, formatMxn } from "@/components/reports/KpiCard";
import { ShareExportPanel } from "@/components/reports/ShareExportPanel";

export default function CashFlowReport() {
  const { isSuperAdmin, activeTenantId } = useTenant();
  const [range, setRange] = useState(() => ({ preset: "month", ...presetRange("month") }));
  const [branchId, setBranchId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const params = useMemo(() => {
    const p = {};
    if (range.date_from) p.date_from = range.date_from;
    if (range.date_to) p.date_to = range.date_to;
    if (branchId) p.branch_id = branchId;
    if (isSuperAdmin && activeTenantId) p.tenant_id = activeTenantId;
    return p;
  }, [range, branchId, isSuperAdmin, activeTenantId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data: d } = await api.get("/reports/cash-flow", { params });
        if (!cancelled) setData(d);
      } catch (_) {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [params]);

  const rows = data?.rows || [];
  const totals = data?.totals || {};

  return (
    <div className="space-y-6" data-testid="page-cash-flow">
      <PageHeader
        title="Flujo diario de caja"
        description="Ingresos por ventas cerradas y anticipos recibidos por día."
        actions={
          <ShareExportPanel
            reportKey="cash_flow"
            reportName="Flujo diario de caja"
            endpoint="/reports/cash-flow"
            params={params}
            exportPermission="reports:export"
            filename="flujo-caja"
            branchId={branchId || null}
            filters={{
              date_from: range.date_from,
              date_to: range.date_to,
              branch_name: getActiveBranchName(),
            }}
            stats={{
              sales_closed: totals.sales_total,
              payments_received: totals.payments_total,
              expenses: totals.expenses_total,
              net_operating: totals.net_operating_total,
              net_cash: totals.net_cash_total,
              row_count: rows.length,
            }}
            testIdPrefix="cash-flow-share"
          />
        }
      />

      <Card className="border-border">
        <CardContent className="p-4 flex flex-col lg:flex-row gap-4 lg:items-end lg:justify-between">
          <DateRangeFilter value={range} onChange={setRange} />
          <BranchFilter value={branchId} onChange={setBranchId} />
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="cash-flow-kpis">
        <KpiCard label="Ventas totales" value={formatMxn(totals.sales_total)} loading={loading} testId="cf-sales" />
        <KpiCard label="Anticipos totales" value={formatMxn(totals.payments_total)} loading={loading} testId="cf-payments" />
        <KpiCard label="Gastos aprobados" value={formatMxn(totals.expenses_total)} loading={loading} testId="cf-expenses" />
        <KpiCard
          label="Resultado operativo"
          value={formatMxn(totals.net_operating_total)}
          hint="Ventas − Gastos"
          loading={loading}
          testId="cf-net-operating"
          tone={totals.net_operating_total < 0 ? "border-red-200 bg-red-50/30" : ""}
        />
        <KpiCard
          label="Flujo neto de caja"
          value={formatMxn(totals.net_cash_total)}
          hint="Cobros − Gastos"
          loading={loading}
          testId="cf-net-cash"
          tone={totals.net_cash_total < 0 ? "border-red-200 bg-red-50/30" : ""}
        />
      </div>

      <Card className="border-border" data-testid="cash-flow-chart">
        <CardHeader className="border-b border-border">
          <CardTitle className="tracking-tight">Evolución diaria</CardTitle>
          <CardDescription>Comparativo ventas vs anticipos, línea acumulada.</CardDescription>
        </CardHeader>
        <CardContent className="pt-6">
          {loading ? (
            <Skeleton className="h-64" />
          ) : (
            <div style={{ width: "100%", height: 320 }}>
              <ResponsiveContainer>
                <LineChart data={rows} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
                  <CartesianGrid stroke="#eef2f7" strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(v) => v.slice(5)} />
                  <YAxis tick={{ fontSize: 11 }} width={70}
                    tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v)} />
                  <Tooltip formatter={(v) => formatMxn(v)} labelFormatter={(l) => `Día ${l}`} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Line name="Ventas cerradas" type="monotone" dataKey="sales_closed" stroke="var(--tenant-primary)" strokeWidth={2} dot={false} />
                  <Line name="Anticipos" type="monotone" dataKey="payments_received" stroke="#22c55e" strokeWidth={2} dot={false} />
                  <Line name="Gastos" type="monotone" dataKey="expenses" stroke="#ef4444" strokeWidth={2} dot={false} />
                  <Line name="Acum. anticipos" type="monotone" dataKey="cum_payments" stroke="#94a3b8" strokeWidth={1} strokeDasharray="4 4" dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-border" data-testid="cash-flow-table">
        <CardHeader className="border-b border-border">
          <CardTitle className="tracking-tight">Detalle por día</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto max-h-96">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Fecha</TableHead>
                  <TableHead className="text-right">Ventas cerradas</TableHead>
                  <TableHead className="text-right">Anticipos</TableHead>
                  <TableHead className="text-right">Gastos</TableHead>
                  <TableHead className="text-right">Resultado operativo</TableHead>
                  <TableHead className="text-right">Flujo neto caja</TableHead>
                  <TableHead className="text-right">Acum. ventas</TableHead>
                  <TableHead className="text-right">Acum. anticipos</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.date} data-testid={`cf-row-${r.date}`}>
                    <TableCell className="font-mono text-xs">{r.date}</TableCell>
                    <TableCell className="text-right">{formatMxn(r.sales_closed)}</TableCell>
                    <TableCell className="text-right">{formatMxn(r.payments_received)}</TableCell>
                    <TableCell className="text-right text-red-700">{formatMxn(r.expenses)}</TableCell>
                    <TableCell className={`text-right font-medium ${r.net_operating < 0 ? "text-red-700" : "text-emerald-700"}`}>
                      {formatMxn(r.net_operating)}
                    </TableCell>
                    <TableCell className={`text-right font-medium ${r.net_cash < 0 ? "text-red-700" : "text-emerald-700"}`}>
                      {formatMxn(r.net_cash)}
                    </TableCell>
                    <TableCell className="text-right text-muted-foreground">{formatMxn(r.cum_sales)}</TableCell>
                    <TableCell className="text-right text-muted-foreground">{formatMxn(r.cum_payments)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
